<template>
  <div class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden">
    <div class="px-5 py-3.5 border-b border-gray-100 dark:border-gray-700">
      <p class="text-sm font-semibold text-gray-900 dark:text-white">Booking Activity</p>
      <p class="text-xs text-gray-400 dark:text-gray-500 mt-0.5">Bookings by day of the week</p>
    </div>

    <div v-if="loading && !chartData" class="animate-pulse p-4 h-64 flex items-end gap-2">
      <div v-for="i in 7" :key="i" class="flex-1 bg-gray-100 dark:bg-gray-800 rounded-t"
        :style="{ height: `${[40, 70, 55, 85, 60, 45, 30][i - 1]}%` }"></div>
    </div>

    <div v-else-if="isEmpty" class="flex flex-col items-center justify-center py-16 text-center px-4">
      <div class="w-12 h-12 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-3">
        <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5"/>
        </svg>
      </div>
      <p class="text-sm font-semibold text-gray-700 dark:text-gray-200">No bookings yet</p>
      <p class="text-xs text-gray-400 dark:text-gray-500 mt-1">Booking activity will appear here.</p>
    </div>

    <div v-else ref="chartEl" class="p-4 h-64"></div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue'
import { useAnalyticsBookingsStore } from 'stores/analyticsBookings.js'

const props = defineProps({ duration: { type: String, required: true } })
const store = useAnalyticsBookingsStore()

const chartEl  = ref(null)
const loading  = computed(() => store.popularDaysLoading)
const chartData = computed(() => store.popularDays[props.duration] ?? null)
const isEmpty  = computed(() => {
  const series = chartData.value?.series?.[0]?.data
  return Array.isArray(series) && series.every(d => d.y === 0)
})

let chart = null

function renderChart() {
  if (!chartEl.value || !chartData.value?.series) return
  if (chart) { chart.destroy(); chart = null }
  chart = Highcharts.chart(chartEl.value, {
    chart: { type: 'column', backgroundColor: 'transparent' },
    title: { text: null },
    credits: { enabled: false },
    xAxis: { type: 'category', lineColor: '#e5e7eb', tickColor: '#e5e7eb', labels: { style: { color: '#6b7280', fontSize: '11px' } } },
    yAxis: { title: { text: null }, gridLineColor: '#f3f4f6', labels: { style: { color: '#6b7280', fontSize: '11px' } } },
    legend: { enabled: false },
    tooltip: {
      backgroundColor: '#1f2937',
      borderColor: '#374151',
      borderRadius: 8,
      style: { color: '#f9fafb', fontSize: '12px' },
      pointFormat: '<b>{point.name}</b>: {point.y} bookings',
    },
    plotOptions: {
      series: {
        borderWidth: 0,
        borderRadius: 4,
        colorByPoint: true,
        colors: ['#2563eb', '#10b981', '#8b5cf6', '#f59e0b', '#ef4444', '#06b6d4', '#ec4899'],
        dataLabels: { enabled: false },
      },
    },
    series:   chartData.value.series,
    drilldown: chartData.value.drilldown,
  })
}

watch(chartData, async (val) => {
  if (val) { await nextTick(); renderChart() }
})

onMounted(() => {
  if (chartData.value) renderChart()
})

onUnmounted(() => {
  if (chart) { chart.destroy(); chart = null }
})
</script>
